import * as React from "react";
import { Check, Loader2, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * AsyncSearchableSelect - Server-side searchable dropdown for large datasets
 * 
 * Features:
 * - Server-side search with debounce (300ms)
 * - Supports 18,000+ item catalogs
 * - Shows loading state during search
 * - Initial items loaded on focus (optional)
 * - Keyboard navigation
 * - Optional inline create: shows "Create {typed}" when no exact match
 * 
 * @param {Object} props
 * @param {function} props.onSearch - Async function(query) that returns [{value, label, sublabel?}]
 * @param {string} props.value - Selected value
 * @param {function} props.onValueChange - Callback when value changes
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.emptyText - Text shown when no results
 * @param {string} props.className - Additional classes
 * @param {boolean} props.disabled - Disable the select
 * @param {number} props.debounceMs - Debounce delay (default 300ms)
 * @param {boolean} props.loadOnFocus - Load initial results on focus (default true)
 * @param {boolean} props.allowCreate - Show "+ Create" option when no exact match (default false)
 * @param {function} props.onCreateSelected - Callback(name) when create option is selected
 * @param {string} props.focusHint - Hint shown when dropdown is open but no query typed yet
 */
export function AsyncSearchableSelect({
  onSearch,
  searchFn,
  value,
  onValueChange,
  placeholder = "Search...",
  emptyText = "No results found.",
  focusHint = "Type to search...",
  className,
  disabled = false,
  debounceMs = 300,
  loadOnFocus = true,
  allowCreate = false,
  onCreateSelected,
  ...props
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const [selectedLabel, setSelectedLabel] = React.useState("");
  
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const abortRef = React.useRef(null);

  // Support both prop names
  const searchHandler = onSearch || searchFn;

  // Track if we've loaded initial results
  const initialLoadDone = React.useRef(false);

  // Compute display options: existing options + optional "Create" entry
  const CREATE_PREFIX = "__create__:";
  const trimmedInput = inputValue.trim();
  const showCreateOption = allowCreate
    && trimmedInput.length >= 3
    && !loading
    && !options.some(opt => {
      // Extract raw name from label (strip SKU suffix like " (SKU123)")
      const rawLabel = opt.label.replace(/\s*\([^)]*\)\s*$/, "");
      return rawLabel.toLowerCase() === trimmedInput.toLowerCase();
    });

  const displayOptions = React.useMemo(() => {
    if (!showCreateOption) return options;
    return [
      ...options,
      {
        value: `${CREATE_PREFIX}${trimmedInput}`,
        label: trimmedInput,
        isCreate: true,
      },
    ];
  }, [options, showCreateOption, trimmedInput]);

  // Perform search with debounce
  const performSearch = React.useCallback(async (query) => {
    // Cancel previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setLoading(true);
    try {
      const results = await searchHandler(query, abortRef.current.signal);
      if (results) {
        setOptions(results);
        setHighlightedIndex(0);
        
        // Update selected label if value matches
        if (value) {
          const selected = results.find(opt => opt.value === value);
          if (selected) {
            setSelectedLabel(selected.label);
          }
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [searchHandler, value]);

  // Debounced search
  const debouncedSearch = React.useCallback((query) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, debounceMs);
  }, [performSearch, debounceMs]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!open) setOpen(true);
    debouncedSearch(newValue);
  };

  // Handle focus - load initial results
  const handleInputFocus = () => {
    if (!disabled) {
      setOpen(true);
      setInputValue("");
      if (loadOnFocus && !initialLoadDone.current) {
        performSearch("");
        initialLoadDone.current = true;
      } else if (options.length === 0) {
        performSearch("");
      }
    }
  };

  // Handle click
  const handleInputClick = () => {
    if (!disabled && !open) {
      setOpen(true);
      setInputValue("");
      if (options.length === 0) {
        performSearch("");
      }
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    if (option.isCreate && onCreateSelected) {
      const createName = option.value.replace(CREATE_PREFIX, "");
      justCreatedRef.current = true;
      setSelectedLabel(createName);
      onCreateSelected(createName);
      setOpen(false);
      setInputValue("");
      inputRef.current?.blur();
      return;
    }
    onValueChange(option.value);
    setSelectedLabel(option.label);
    setOpen(false);
    setInputValue("");
    inputRef.current?.blur();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!open) {
          setOpen(true);
          if (options.length === 0) performSearch("");
        } else {
          setHighlightedIndex((prev) =>
            prev < displayOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (open) {
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (open && displayOptions[highlightedIndex] && !displayOptions[highlightedIndex].isCreate) {
          handleSelect(displayOptions[highlightedIndex]);
        } else if (!open) {
          setOpen(true);
          if (options.length === 0) performSearch("");
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        setInputValue("");
        break;
      case "Tab":
        if (open) {
          setOpen(false);
          setInputValue("");
        }
        break;
      default:
        break;
    }
  };

  // Handle click outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
        setInputValue("");
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (open && listRef.current) {
      const highlightedEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, open]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  // Clear selected label when value is externally reset (but not after create)
  const justCreatedRef = React.useRef(false);
  React.useEffect(() => {
    if (!value && !justCreatedRef.current) {
      setSelectedLabel("");
    }
    justCreatedRef.current = false;
  }, [value]);

  // Display value
  const displayValue = open ? inputValue : selectedLabel;

  // Toggle dropdown
  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      if (open) {
        setOpen(false);
        setInputValue("");
      } else {
        setOpen(true);
        inputRef.current?.focus();
        if (options.length === 0) performSearch("");
      }
    }
  };

  // Clear selection
  const handleClear = () => {
    onValueChange("");
    setSelectedLabel("");
    setInputValue("");
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      {...props}
    >
      {/* Input container */}
      <div
        className={cn(
          "flex h-9 w-full items-center rounded-md border border-input bg-transparent text-sm shadow-sm transition-colors overflow-hidden",
          "focus-within:ring-1 focus-within:ring-ring",
          disabled && "cursor-not-allowed opacity-50",
          open && "ring-1 ring-ring"
        )}
      >
        <Search className="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "flex-1 h-full px-2 py-2 bg-transparent outline-none placeholder:text-muted-foreground",
            "disabled:cursor-not-allowed"
          )}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-autocomplete="list"
        />
        
        {/* Loading indicator */}
        {loading && (
          <Loader2 className="h-4 w-4 text-muted-foreground animate-spin mr-2" />
        )}
        
        {/* Clear button when value is selected */}
        {value && !disabled && !loading && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            className="px-2 h-full flex items-center justify-center hover:bg-muted/50 transition-colors"
            tabIndex={-1}
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Dropdown list */}
      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1.5 w-full rounded-lg border border-border/80 bg-popover shadow-lg shadow-black/8 ring-1 ring-black/[0.04]",
            "animate-in fade-in-0 zoom-in-95"
          )}
        >
          <ul
            ref={listRef}
            role="listbox"
            className="max-h-[280px] overflow-y-auto p-1"
          >
            {loading && options.length === 0 ? (
              <li className="py-3 px-2 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </li>
            ) : displayOptions.length === 0 ? (
              <li className="py-3 px-2 text-center text-sm text-muted-foreground">
                {inputValue ? emptyText : focusHint}
              </li>
            ) : (
              displayOptions.map((option, index) => (
                <li
                  key={option.value}
                  data-index={index}
                  role="option"
                  aria-selected={value === option.value}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none",
                    "transition-colors",
                    highlightedIndex === index && "bg-accent text-accent-foreground",
                    value === option.value && "font-medium",
                    option.isCreate && "border-t mt-1 pt-2"
                  )}
                  data-testid={option.isCreate ? "create-new-part-option" : undefined}
                >
                  {option.isCreate ? (
                    <>
                      <Plus className="mr-2 h-4 w-4 shrink-0 text-primary" />
                      <div className="flex-1 min-w-0">
                        <div className="truncate text-primary font-medium">
                          Create &ldquo;{option.label}&rdquo;
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          Add new part to catalogue
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{option.label}</div>
                        {option.sublabel && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.sublabel}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </li>
              ))
            )}
          </ul>
          {!loading && displayOptions.length > 0 && (
            <div className="px-2 py-1.5 text-xs text-muted-foreground border-t bg-muted/30 rounded-b-lg">
              {options.length} result{options.length !== 1 ? 's' : ''} • Type to search more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AsyncSearchableSelect;
